import React, { useState } from 'react'
import PropTypes from 'prop-types';
import { Row, Col } from "react-bootstrap";
import NumberFormat from 'react-number-format';
import { Button, IconButton, Dialog, DialogContent, makeStyles, Card } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { benefitDurationListToShowAsterisk, getAsteriskNotationText } from '../../SubComponents/SelectOptions';
import './QuoteCard.scss';

const useStyles = makeStyles((theme) => ({
  colorPrimary: {
    color: theme.palette.primary.main,
  }
}));

export default function QuoteCard(props) {
  const { logoUrl, productName, monthlyRate, rating, monthlyBenefit, deathBenefit, minDeathBenefit, totalBenefit, quoteResults, isSurvivorshipPolicyType, insuredBasicInfo, isJointPolicy, clientCoverage } = props;

  const classes = useStyles();
  const [isProductDetailsDialogOpen, setIsProductDetailsDialogOpen] = useState(false);
  const isJointPoliciesEnable = isSurvivorshipPolicyType && isJointPolicy;
  const isShowAsterisk = benefitDurationListToShowAsterisk.includes(clientCoverage.benefitDuration);

  const pi1RateDetails = {
    monthlyBenefit: quoteResults.initialMonthlyLTCBenefit,
    totalLTCBenefit: quoteResults.initialTotalLTCBenefit,
    deathBenefit: quoteResults.initialDeathBenefit,
    endOfYearCoverage: quoteResults.endOfYearCoverage,
    monthlyRate: quoteResults.initialMonthlyLTCBenefit,
    insuredName: `${insuredBasicInfo.insuredInfo[0].proposedInsuredFirstName} ${insuredBasicInfo.insuredInfo[0].proposedInsuredLastName}`
  }

  const pi2RateDetails = {
    monthlyBenefit: quoteResults.insured2InitialMonthlyLTCBenefit,
    totalLTCBenefit: quoteResults.insured2InitialTotalLTCBenefit,
    deathBenefit: quoteResults.insured2InitialDeathBenefit,
    endOfYearCoverage: quoteResults.insured2EndOfYearCoverage,
    monthlyRate: quoteResults.insured2InitialMonthlyLTCBenefit,
    insuredName: isSurvivorshipPolicyType && `${insuredBasicInfo.insuredInfo[1].proposedInsuredFirstName} ${insuredBasicInfo.insuredInfo[1].proposedInsuredLastName}`
  }

  return (
    <div className={`abltc-quote-card pl-2 mb-3 ${isSurvivorshipPolicyType && isJointPolicy ? 'joint-policy-card' : ''}`}>
      <Row>

        <Col xs={12} className='pl-0'>
          <Row className='no-gutters'>
            <Col sm={2} className="d-flex align-items-start flex-column mr-2 quote-logo">
              <div className="d-flex flex-column w-100">
                <img className="carrier-logo-lg" src={logoUrl} alt={`${productName} Logo`} />
              </div>
            </Col>
            <Col>
              <h4><b>{productName}</b></h4>
              {rating && <h5 className={classes.colorPrimary}><b>{rating} Rating</b></h5>}
              {isJointPoliciesEnable && <h5 className='text-gray'>Total Joint Premium:&nbsp;
                <NumberFormat
                  className='quote-amount-dollars-small'
                  value={quoteResults.premium}
                  displayType={'text'}
                  thousandSeparator={true}
                  fixedDecimalScale={true}
                />
              </h5>}
            </Col>
            <Col xs='auto p-0 mr-2'>
              <p className='text-design-LTC mt-2'>Total{isSurvivorshipPolicyType ? " Monthly " : " "}LTC Benefit</p>
              <p className='text-design-LTC float-right'>Day 1</p>
            </Col>
            {((isJointPoliciesEnable) || (!isSurvivorshipPolicyType)) &&
              <Col xs="auto">
                <p>
                  <NumberFormat
                    className={`quote-amount-dollars ${classes.colorPrimary}`}
                    value={!isJointPoliciesEnable ? monthlyRate : quoteResults.initialMonthlyLTCBenefit}
                    displayType={'text'}
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={true}
                  />
                </p>
              </Col>}
          </Row>
          {isSurvivorshipPolicyType ?
            (isJointPolicy ?
              <SurvivorshipPolicyRateDetail data={pi1RateDetails} quoteResults={quoteResults} isJointPolicy={isJointPolicy} insuredBasicInfo={insuredBasicInfo} isShowAsterisk={false} />
              :
              <React.Fragment>
                <SurvivorshipPolicyRateDetail data={pi1RateDetails} quoteResults={quoteResults} isJointPolicy={isJointPolicy} insuredBasicInfo={insuredBasicInfo} isShowAsterisk={isShowAsterisk} />
                {!(quoteResults.insured2EndOfYearCoverage === null && quoteResults.insured2GuaranteedMinDeathBenefit === null && quoteResults.insured2InitialDeathBenefit === null && quoteResults.insured2InitialMonthlyLTCBenefit === null && quoteResults.insured2InitialTotalLTCBenefit === null) &&
                  <SurvivorshipPolicyRateDetail data={pi2RateDetails} quoteResults={quoteResults} isJointPolicy={isJointPolicy} insuredBasicInfo={insuredBasicInfo} isShowAsterisk={isShowAsterisk} />}

              </React.Fragment>)

            :
            (<Row className='no-gutters ml-5 text-design mt-4'>
              <Col sm='auto' className='mr-3'>
                <div>
                  <b>Day 1 monthly LTC Benefit</b>
                </div>
                <p>
                  <NumberFormat
                    className="quote-amount-dollars-small"
                    value={monthlyBenefit}
                    displayType={'text'}
                    thousandSeparator={true}
                    decimalScale={0}
                    fixedDecimalScale={true}
                  />
                </p>
              </Col>
              <Col sm='auto' className='mr-3'>
                <div>
                  <b>Day 1 Death Benefit</b>
                </div>
                <p>
                  <NumberFormat
                    className="quote-amount-dollars-small"
                    value={deathBenefit}
                    displayType={'text'}
                    thousandSeparator={true}
                    decimalScale={0}
                    fixedDecimalScale={true}
                  />
                </p>
              </Col>
              <Col sm='auto' className=' mr-3'>
                <div>
                  <b>Guaranteed Min Death Benefit</b>
                </div>
                <p className="quote-amount-small">
                  {minDeathBenefit}
                </p>
              </Col>
              <Col sm='auto'>
                <div>
                  <b>Total LTC Benefit Age 80</b>
                </div>
                <p>
                  <NumberFormat
                    className="quote-amount-dollars-small"
                    value={totalBenefit}
                    displayType={'text'}
                    thousandSeparator={true}
                    decimalScale={0}
                    fixedDecimalScale={true}
                  />
                </p>
              </Col>
            </Row>)}
          <Row className={`abltc-quote-details-section`}>
            <Col sm={{ span: "auto", offset: 5 }} className="d-flex justify-content-center mt-3">
              <Button variant="text" className={classes.colorPrimary} onClick={() => setIsProductDetailsDialogOpen(true)}>
                <b>PRODUCT DETAILS</b>
              </Button>
            </Col>
            {(isShowAsterisk && !props.isJointPolicy) && <Col><div className='col mt-3 ml-n4 text-right asterisk-notation-text'><span className='sup-text'>*</span>{getAsteriskNotationText(clientCoverage.benefitDuration)}</div></Col>}
          </Row>

        </Col>

      </Row>

      <ProductDetailsDialog
        isOpen={isProductDetailsDialogOpen}
        quoteCardProps={props}
        onClose={() => setIsProductDetailsDialogOpen(false)}
        isSurvivorshipPolicyType={isSurvivorshipPolicyType}
        isJointPolicy={isJointPolicy}
        insuredBasicInfo={insuredBasicInfo}
      />
    </div>
  )
}

const SurvivorshipPolicyRateDetail = (props) => {
  const { data, quoteResults } = props
  const classes = useStyles();
  const insuredDetails = props.insuredBasicInfo.insuredInfo;

  return (<Row className='no-gutters ml-2 text-design mt-4 mb-4'>
    <Col sm={2} className='mr-3 w-100 d-flex justify-content-center align-items-center'>
      <div className='insured-name text-secondary text-break pl-2 text-center'>
        {props.isJointPolicy
          ? props.insuredBasicInfo && <span> {insuredDetails[0].proposedInsuredFirstName} {insuredDetails[0].proposedInsuredLastName} and <br />
            {insuredDetails[1].proposedInsuredFirstName} {insuredDetails[1].proposedInsuredLastName} </span>
          : <span> {data.insuredName} <br />
            <b className='text-dark'>
              <NumberFormat
                className="quote-amount-dollars-small amount-dollars-black"
                value={quoteResults.premium}
                displayType={'text'}
                thousandSeparator={true}
                decimalScale={0}
                fixedDecimalScale={true}
              />
            </b> </span>
        }
      </div>
    </Col>
    <Col className='mr-3' sm={5}>
      <fieldset className='info-box'>
        <legend>Day 1</legend>
        <div className='d-flex pb-1 pl-2'>
          <div className='mr-3'>
            <div>
              <b className='text-gray'>Total LTC Benefit</b>
            </div>
            <div>
              <NumberFormat
                className="quote-amount-dollars-small amount-dollars-black"
                value={data.totalLTCBenefit}
                displayType={'text'}
                thousandSeparator={true}
                decimalScale={0}
                fixedDecimalScale={true}
              />
            </div>
          </div>
          <div className='mr-3'>
            <div>
              <b className='text-gray'>Monthly LTC Benefit</b>
            </div>
            <div>
              <NumberFormat
                className="quote-amount-dollars-small amount-dollars-black"
                value={data.monthlyBenefit}
                displayType={'text'}
                thousandSeparator={true}
                decimalScale={0}
                fixedDecimalScale={true}
              />
            </div>
          </div>
          <div className='mr-3'>
            <div>
              <b className='text-gray'>Death Benefit</b>
            </div>
            <div>
              <NumberFormat
                className="quote-amount-dollars-small amount-dollars-black"
                value={data.deathBenefit}
                displayType={'text'}
                thousandSeparator={true}
                decimalScale={0}
                fixedDecimalScale={true}
              />
            </div>
          </div>
        </div>
      </fieldset>
    </Col>
    <Col sm={2}>
      <fieldset className='info-box'>
        <legend>Age 80</legend>
        <div className='pb-1 pl-2'>
          <div>
            <b className='text-gray'>Total LTC Benefit</b>
          </div>
          <div>
            <NumberFormat
              className="quote-amount-dollars-small amount-dollars-black"
              value={data.endOfYearCoverage}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={0}
              fixedDecimalScale={true}
            />
          </div>
        </div>
      </fieldset>
    </Col>
    {
      !props.isJointPolicy &&
      <Col className='w-100 d-flex justify-content-center align-items-center'>
        <p className='m-0'>
          <NumberFormat
            className={`quote-amount-dollars-text ${classes.colorPrimary}`}
            value={data.monthlyRate}
            displayType={'text'}
            thousandSeparator={true}
            decimalScale={2}
            fixedDecimalScale={true}
          />
          {props.isShowAsterisk && <span className={`sup-text primary-color-asterisk ${classes.colorPrimary}`}>*</span>}
        </p>
      </Col>
    }
  </Row>)
}

const ProductDetailsRatingCard = (props) => {
  const classes = useStyles();
  return (
    <Card className="rating-card">
      <div className="rating-card-head">
        <img src={props.logo} alt={props.rater} className="img-fluid" />
      </div>
      <p className="rater">{props.rater}</p>
      <p className={`rating ${classes.colorPrimary}`}>{props.rating}</p>
    </Card>
  );
};
const ProductDetailsDialog = (props) => {

  const classes = useStyles();
  const hasRating =
    props.quoteCardProps.quoteResults.ratingAMBest ||
    props.quoteCardProps.quoteResults.ratingSP ||
    props.quoteCardProps.quoteResults.ratingMoodys ||
    props.quoteCardProps.quoteResults.ratingFitch ||
    props.quoteCardProps.quoteResults.ratingComdex;

  return (
    <Dialog className="abltc-product-details-dialog" open={props.isOpen} onBackdropClick={props.onClose} scroll="body" fullWidth={true} maxWidth="md" aria-labelledby="product-details-dialog">
      <DialogContent className="product-details-dialog-content">
        <IconButton aria-label="close" onClick={props.onClose} className="close-button">
          <CloseIcon />
        </IconButton>
        <img className="carrier-logo-lg" src={props.quoteCardProps.logoUrl} alt={`${props.quoteCardProps.carrierName}`} />

        <p className={`pl-2 mb-4 textRatingBlue ${classes.colorPrimary}`}>
          {props.quoteCardProps.rating}
          <span className="ml-1">Rating</span>
        </p>

        <div className="product">
          <strong>
            <h5>
              <b>
                {props.quoteCardProps.productName}
              </b>
            </h5>
          </strong>
        </div>

        <Row>
          <Col xs="6" md="12">
            <Stats
              monthlyRate={props.quoteCardProps.monthlyRate}
              statsProps={props.quoteCardProps.quoteResults}
              isSurvivorshipPolicyType={props.isSurvivorshipPolicyType}
              isJointPolicy={props.isJointPolicy}
              insuredBasicInfo={props.insuredBasicInfo}
            />
          </Col>
        </Row>

        {hasRating && (
          <div className="ratings">
            <p>
              <h5><strong>Insurance Carrier Ratings</strong></h5>
            </p>
            <ul className="d-flex rating-cards">
              <li>
                {props.quoteCardProps.quoteResults.ratingAMBest && (
                  <ProductDetailsRatingCard logo="/logos/ratings/ambest.jpg" rater="AM Best" rating={props.quoteCardProps.quoteResults.ratingAMBest} />
                )}
              </li>
              <li>
                {props.quoteCardProps.quoteResults.ratingSP && <ProductDetailsRatingCard logo="/logos/ratings/sp.jpg" rater="S&amp;P Global" rating={props.quoteCardProps.quoteResults.ratingSP} />}
              </li>
              <li>
                {props.quoteCardProps.quoteResults.ratingMoodys && (
                  <ProductDetailsRatingCard logo="/logos/ratings/moodys.jpg" rater="Moody's" rating={props.quoteCardProps.quoteResults.ratingMoodys} />
                )}
              </li>
              <li>
                {props.quoteCardProps.quoteResults.ratingFitch && <ProductDetailsRatingCard logo="/logos/ratings/fitch.jpg" rater="Fitch" rating={props.quoteCardProps.quoteResults.ratingFitch} />}
              </li>
              <li>
                {props.quoteCardProps.quoteResults.ratingComdex && (
                  <ProductDetailsRatingCard logo="/logos/ratings/comdex.jpg" rater="Comdex" rating={props.quoteCardProps.quoteResults.ratingComdex} />
                )}
              </li>
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Stats = (props) => {
  const classes = useStyles();
  const isJointPoliciesEnable = props.isSurvivorshipPolicyType && props.isJointPolicy;
  const isIndividualPoliciesEnable = props.isSurvivorshipPolicyType && !props.isJointPolicy;
  const insuredDetails = props.insuredBasicInfo.insuredInfo;

  return (
    <div className="abltc-product-details-stat">
      <Row>
        {(isJointPoliciesEnable || (!props.isSurvivorshipPolicyType)) &&
          <Col>
            <p>
              <NumberFormat
                className={`quote-amount-dollars ${classes.colorPrimary}`}
                value={props.monthlyRate}
                displayType={'text'}
                thousandSeparator={true}
                decimalScale={0}
                fixedDecimalScale={true}
              />
              <span className='ml-2'>Total LTC Benefit Day 1</span>
            </p>
          </Col>}
        {isIndividualPoliciesEnable && <React.Fragment>
          <Col className='d-flex align-items-center'>
            <p className='mt-3 paragraph-space'>Total LTC Benefit Day 1</p>
          </Col>
          <Col className='text-center'>
            <p className='paragraph-space'>{insuredDetails[0].proposedInsuredFirstName} {insuredDetails[0].proposedInsuredLastName}</p>
            <p className='paragraph-space'>
              <NumberFormat
                className={`quote-amount-dollars ${classes.colorPrimary}`}
                value={props.statsProps.initialTotalLTCBenefit}
                displayType={'text'}
                thousandSeparator={true}
                decimalScale={0}
                fixedDecimalScale={true}
              />
            </p>
          </Col>
          <Col className='text-center'>
            <p className='paragraph-space'>{insuredDetails[1].proposedInsuredFirstName} {insuredDetails[1].proposedInsuredLastName}</p>
            <p className='paragraph-space'>
              <NumberFormat
                className={`quote-amount-dollars ${classes.colorPrimary}`}
                value={props.statsProps.insured2InitialTotalLTCBenefit}
                displayType={'text'}
                thousandSeparator={true}
                decimalScale={0}
                fixedDecimalScale={true}
              />
            </p>
          </Col>
        </React.Fragment>}
      </Row>
      <Row className='ml-2 mb-2'>
        Day 1 of Eligibility
      </Row>
      <Row>
        {props.isSurvivorshipPolicyType &&
          <Col className="ml-2" sm='auto'>
            <b className="text-design"> Proposed Insured</b>
            {!props.isJointPolicy ?
              <React.Fragment>
                <p>{insuredDetails[0].proposedInsuredFirstName} {insuredDetails[0].proposedInsuredLastName}</p>
                <p className='mt-n3'>{insuredDetails[1].proposedInsuredFirstName} {insuredDetails[1].proposedInsuredLastName}</p>
              </React.Fragment> :
              props.insuredBasicInfo && <p> {insuredDetails[0].proposedInsuredFirstName} {insuredDetails[0].proposedInsuredLastName} and <br />
                {insuredDetails[1].proposedInsuredFirstName} {insuredDetails[1].proposedInsuredLastName} </p>}
          </Col>}
        <Col className="ml-2" sm='auto'>
          <b className="text-design">LTC Benefit</b>
          <p>
            <NumberFormat
              className="quote-amount-dollars-small"
              value={props.statsProps.initialTotalLTCBenefit}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={0}
              fixedDecimalScale={true}
            />
          </p>
          {isIndividualPoliciesEnable && <p className='paragraph-space mt-n3'>
            <NumberFormat
              className="quote-amount-dollars-small"
              value={props.statsProps.insured2InitialTotalLTCBenefit}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={0}
              fixedDecimalScale={true}
            />
          </p>}
        </Col>
        <Col sm='auto'>
          <b className="text-design">Monthly LTC Benefit</b>
          <p>
            <NumberFormat
              className="quote-amount-dollars-small"
              value={props.statsProps.initialMonthlyLTCBenefit}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={0}
              fixedDecimalScale={true}
            />
          </p>
          {isIndividualPoliciesEnable && <p className='paragraph-space mt-n3'>
            <NumberFormat
              className="quote-amount-dollars-small"
              value={props.statsProps.insured2InitialMonthlyLTCBenefit}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={0}
              fixedDecimalScale={true}
            />
          </p>}
        </Col>
        <Col sm='auto'>
          <b className="text-design">Death Benefit</b>
          <p>
            <NumberFormat
              className="quote-amount-dollars-small"
              value={props.statsProps.initialDeathBenefit}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={0}
              fixedDecimalScale={true}
            />
          </p>
          {isIndividualPoliciesEnable && <p className='paragraph-space mt-n3'>
            <NumberFormat
              className="quote-amount-dollars-small"
              value={props.statsProps.insured2InitialDeathBenefit}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={0}
              fixedDecimalScale={true}
            />
          </p>}
        </Col>
        {(!props.isSurvivorshipPolicyType) &&
          <Col sm='auto'>
            <b className="text-design">Guaranteed Min. Death Benefit</b>
            <p className={`stat-text`}>
              {props.statsProps.guaranteedMinDeathBenefit}
            </p>
          </Col>}
        <Col sm='auto'>
          <b className="text-design">Total LTC Benefit Age 80</b>
          <p>
            <NumberFormat
              className="quote-amount-dollars-small"
              value={props.statsProps.endOfYearCoverage}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={0}
              fixedDecimalScale={true}
            />
          </p>
          {isIndividualPoliciesEnable && <p className='paragraph-space mt-n3'>
            <NumberFormat
              className="quote-amount-dollars-small"
              value={props.statsProps.insured2EndOfYearCoverage}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={0}
              fixedDecimalScale={true}
            />
          </p>}
        </Col>
      </Row>
      <Row className='ml-2 mb-2'>
        Available Options
      </Row>
      <Row className='no-gutters'>
        <Col className="ml-2 mr-3" sm='auto'>
          <b className="text-design">Max Death Benefit</b>
          <p>
            <NumberFormat
              className="quote-amount-dollars-small"
              value={props.statsProps.maxDeathBenefit}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={0}
              fixedDecimalScale={true}
            />
          </p>
        </Col>
        <Col sm='auto' className='mr-3'>
          <b className="text-design">Benefit Duration Periods</b>
          <p className={`stat-text`}>{props.statsProps.benefitDurationPeriod}</p>
        </Col>
        <Col sm={2} className='mr-3'>
          <b className="text-design">Inflation Benefit Options</b>
          <p className={`stat-text`}>{props.statsProps.inflationBenefitOption}</p>
        </Col>
        <Col sm={2} className='mr-3'>
          <b className="text-design">Premium Mode</b>
          <p className={`stat-text`}>{props.statsProps.premiumModeOption}</p>
        </Col>
        <Col sm={2}>
          <b className="text-design">Return of Premium</b>
          <p className={`stat-text`}>
            {props.statsProps.returnOfPremium}
          </p>
        </Col>
      </Row>
    </div>
  );
};
QuoteCard.propTypes = {
  logoUrl: PropTypes.string,
  productName: PropTypes.string,
  monthlyRate: PropTypes.string,
  rating: PropTypes.string,
  monthlyBenefit: PropTypes.string,
  deathBenefit: PropTypes.string,
  minDeathBenefit: PropTypes.string,
  totalBenefit: PropTypes.string,
  quoteResults: PropTypes.object,
}



// WEBPACK FOOTER //
// ./src/components/Product/ABLTC/RequestIllustration/SubComponents/QuoteCard.js